import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PvNetMeteringService {

  constructor() { }

  /** Extracts and reverses bucketed device data */
  extractDeviceData(buckets: any[], deviceList: string[]): any[][] {
    const deviceData: any[][] = [];
    for (const deviceKey of deviceList) {
      const found = buckets.find((b:any) => b.key == deviceKey);
      if (found?.date_agg?.buckets) {
        deviceData.push(found.date_agg.buckets.reverse());
      }
    }
    return deviceData;
  }

  /** Aggregates multiple devices’ data by time (sum of values) */
  aggregateDeviceData(deviceData: any[][]): any[] {
    if (!deviceData?.length) return [];
  
    const merged = deviceData.reduce((acc, d) => acc.concat(d), []);
    const timestamps = [...new Set(merged.map(d => d.key_as_string))].sort();
    const sumMap = new Map<string, number>();
  
    merged.forEach(d => {
      sumMap.set(d.key_as_string, (sumMap.get(d.key_as_string) ?? 0) + (d.val_total_w?.value ?? 0));
    });
  
    return timestamps.map(ts => ({
      key_as_string: ts,
      val_total_w: {
        value: deviceData.every(dev => dev.some(d => d.key_as_string === ts))
          ? sumMap.get(ts) ?? 0
          : merged.find(d => d.key_as_string === ts)?.val_total_w?.value ?? 0
      }
    }));
  }

  /** Generates timestamp list (every 5 minutes) */
  generateTimestamps(lastTimestamp?: string): { hours: string; minutes: string }[] {
    const timestamps: any[] = [];
    let limitHours = 23;
    let limitMinutes = 55;

    if (lastTimestamp) {
      const date = new Date(lastTimestamp);
      limitHours = date.getHours();
      limitMinutes = date.getMinutes();
    }

    for (let h = 0; h <= limitHours; h++) {
      const maxM = (h === limitHours) ? limitMinutes : 55;
      for (let m = 0; m <= maxM; m += 5) {
        timestamps.push({
          hours: String(h).padStart(2, '0'),
          minutes: String(m).padStart(2, '0'),
        });
      }
    }
    return timestamps;
  }

  /** Matches aggregated data with timestamps to build power array */
  mapToPowerData(timestamps: any[], dataSum: any[], key: string): any[] {
    const result: any[] = [];
    if (!dataSum.length) return result;

    const baseDate = dataSum[0].key_as_string.slice(0, 10);

    for (const time of timestamps) {
      const matched = dataSum.find(
        (d) =>
          d.key_as_string.slice(11, 13) === time.hours &&
          d.key_as_string.slice(14, 16) === time.minutes
      );

      if (matched) {
        result.push({
          date: new Date(matched.key_as_string),
          [key]: matched.val_total_w.value,
        });
      } else {
        result.push({
          date: new Date(`${baseDate}T${time.hours}:${time.minutes}:00.000+05:00`),
          [key]: 0,
        });
      }
    }

    return result;
  }

  /** Combines ke/pv/gs arrays into a unified trend */
  combinePowerData(ke: any[], pv: any[], gs: any[]): any[] {
    const maxLen = Math.max(ke.length, pv.length, gs.length);
    const result: any[] = [];

    for (let i = 0; i < maxLen; i++) {
      const pv_power = pv[i]?.pv_power ?? 0;
      const ke_power = ke[i]?.ke_power ?? 0;
      const gs_power = gs[i]?.gs_power ?? 0;
      const date = ke[i]?.date || pv[i]?.date || gs[i]?.date || null;

      result.push({
        date,
        pv_power,
        ke_power,
        gs_power,
        load_power: pv_power + ke_power + gs_power,
      });
    }

    return result;
  }
}
